import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { InstallButton } from "@/components/InstallPWA";

export default function Login() {
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const go = (user) => navigate(user.role === "student" ? "/app/home" : "/staff/dashboard");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      go(user);
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  const demo = async (role) => {
    setBusy(true);
    try { go(await demoLogin(role)); } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left hero */}
      <div className="relative hidden flex-col justify-between gradient-blue p-12 text-white lg:flex overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(252,99,59,0.35), transparent 70%)" }} />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(102,45,145,0.5), transparent 70%)" }} />
        <div className="relative flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl gradient-orange">
            <Icons.Sparkles size={26} className="text-white" />
          </div>
          <span className="font-head text-2xl">Story Champs</span>
        </div>
        <div className="relative">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="font-head text-6xl leading-[0.95]">
            Your storytelling<br /><span className="text-csh-orange">adventure</span><br />starts here.
          </motion.h1>
          <p className="mt-6 max-w-md text-lg text-white/80">Play. Create. Speak. Perform. Earn Story Points, unlock badges and become a Story Champ! 🌟</p>
          <div className="mt-8 flex gap-4">
            {["Target:Quests", "Medal:Badges", "Trophy:Leaderboard"].map((x) => {
              const [ic, lb] = x.split(":"); const I = Icons[ic];
              return (
                <div key={lb} className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
                  <I size={18} className="text-csh-orange" /> <span className="text-sm font-semibold">{lb}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative text-sm text-white/50">Confident Storytelling Hub · Kids Champ Program</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl gradient-orange">
              <Icons.Sparkles size={20} className="text-white" />
            </div>
            <span className="font-head text-xl text-csh-blue">Story Champs</span>
          </div>
          <h2 className="font-head text-4xl text-csh-blue">Let's go! 👋</h2>
          <p className="mt-1 text-muted-foreground">Sign in to continue your journey.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-bold text-csh-grey">Email</label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@storychamps.com" className="rounded-xl border-2 py-6" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold text-csh-grey">Password</label>
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="rounded-xl border-2 py-6" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full rounded-full gradient-orange py-6 font-head text-lg uppercase tracking-wide text-white hover:scale-[1.02] active:scale-95">
              {busy ? <Icons.Loader2 className="animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs font-bold uppercase text-muted-foreground">
            <div className="h-px flex-1 bg-black/10" /> Demo Mode <div className="h-px flex-1 bg-black/10" />
          </div>
          <div className="mb-4 flex justify-center"><InstallButton /></div>
          <div className="grid grid-cols-3 gap-2">
            {[["student", "Baby", "Student"], ["teacher", "GraduationCap", "Coach"], ["admin", "ShieldCheck", "Admin"]].map(([role, ic, lb]) => {
              const I = Icons[ic];
              return (
                <button key={role} data-testid={`demo-${role}`} onClick={() => demo(role)} disabled={busy}
                  className="flex flex-col items-center gap-1.5 rounded-2xl border-2 border-black/10 bg-white py-4 font-bold text-csh-blue transition-colors hover:border-csh-purple hover:bg-csh-purple/5">
                  <I size={22} className="text-csh-purple" /> <span className="text-xs">{lb}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
